import { Component, useEffect, useId, useRef, useState, type ComponentType, type ReactNode } from 'react';
import { AlertCircleIcon, RefreshCwIcon, WifiOffIcon, XIcon } from 'lucide-react';
import { useLocation } from 'react-router';

import { BrandLockup } from '@/components/common/brand-lockup';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { usePageChrome } from '@/hooks/page-chrome-store';
import { useHasUnsentFolioMessage } from '@/hooks/use-unsent-folio-message';
import {
  isChunkLoadError,
  PageChunkLoadError,
  reloadApp,
  reloadForNewBuild,
  type ChunkReloadOutcome,
} from '@/lib/chunk-load-recovery';
import { hasUnsentFolioMessage } from '@/lib/unsent-folio-message';
import { cn } from '@/lib/utils';

/** A page owns the main region (or the screen); a panel is one region beside other work. */
type RecoverySurface = 'page' | 'panel';

/** A stale build is fixed by a reload; offline by a reload once reconnected; anything else may pass on retry. */
type Failure = 'new-version' | 'offline' | 'error';

interface FailureCopy {
  readonly title: string;
  readonly description: string;
  /**
   * What to say when the chunk failed in place rather than on navigation: the
   * page around it may hold work the user has not saved, so they reload
   * themselves once it is safe.
   */
  readonly inPlaceDescription: string;
  readonly icon: ComponentType<{ className?: string }>;
  readonly iconTone: string;
}

const FAILURE_COPY: Readonly<Record<Failure, FailureCopy>> = {
  'new-version': {
    title: 'A new version of Contract Analyzer is available',
    description: 'Reload to continue with the latest version.',
    inPlaceDescription: 'Save any changes, then reload to continue with the latest version.',
    icon: RefreshCwIcon,
    iconTone: 'bg-accent/10 text-accent',
  },
  offline: {
    title: "You're offline",
    description: 'Reconnect, then reload to continue.',
    inPlaceDescription: 'Reconnect and save any changes, then reload to continue.',
    icon: WifiOffIcon,
    iconTone: 'bg-muted text-muted-foreground',
  },
  error: {
    title: "This page couldn't be displayed",
    description: 'Retry, or reload Contract Analyzer if it keeps happening.',
    inPlaceDescription: 'Retry, or reload Contract Analyzer if it keeps happening.',
    icon: AlertCircleIcon,
    iconTone: 'bg-destructive/10 text-destructive',
  },
};

function failureCopy(failure: Failure, surface: RecoverySurface): FailureCopy {
  const copy = FAILURE_COPY[failure];
  return failure === 'error' && surface === 'panel'
    ? { ...copy, title: "This panel couldn't be displayed" }
    : copy;
}

/**
 * Why a reload is waiting for the user, beyond the failure itself: code that
 * failed in place beside work that may be unsaved, or a navigation held back
 * by a message typed to Sean. `none` is a navigation, or no reload at all.
 */
type ReloadHold = 'none' | 'in-place' | 'folio-message';

/** A navigation held back by a message to Sean, by whether the tab is online to send it. */
const FOLIO_MESSAGE_DESCRIPTION = {
  online: 'Copy or send your message to Sean, then reload.',
  offline: 'Copy your message to Sean, or send it once you reconnect, then reload.',
} as const;

function describe(failure: Failure, copy: FailureCopy, hold: ReloadHold): string {
  if (hold === 'folio-message') {
    return FOLIO_MESSAGE_DESCRIPTION[failure === 'offline' ? 'offline' : 'online'];
  }
  return hold === 'in-place' ? copy.inPlaceDescription : copy.description;
}

/** How the user closes a panel that normally carries its own Close control inside the failed chunk. */
interface RecoveryDismiss {
  readonly label: string;
  readonly onDismiss: () => void;
}

interface RecoveryStateProps {
  readonly failure: Failure;
  readonly reloading: boolean;
  readonly hold: ReloadHold;
  readonly surface: RecoverySurface;
  readonly dismiss: RecoveryDismiss | undefined;
  /** Offered for an `error` failure, which may pass on a second render. */
  readonly onRetry?: () => void;
  readonly onReload: () => void;
}

/** Nothing, or something the user can no longer reach, has focus. */
function isFocusStranded(): boolean {
  const active = document.activeElement;
  return active === null || active === document.body || active.closest('[inert]') !== null;
}

function useDismissOnEscape(onDismiss: (() => void) | undefined): void {
  useEffect(() => {
    if (onDismiss === undefined) {
      return;
    }
    const handleEscape = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.isComposing || event.key !== 'Escape') {
        return;
      }
      onDismiss();
    };
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onDismiss]);
}

function RecoveryState({
  failure,
  reloading,
  hold,
  surface,
  dismiss,
  onRetry,
  onReload,
}: RecoveryStateProps) {
  const isPage = surface === 'page';
  const outsideShell = usePageChrome() === null;
  const headingRef = useRef<HTMLHeadingElement>(null);
  const headingId = useId();
  const descriptionId = useId();
  const copy = failureCopy(failure, surface);
  const Icon = copy.icon;
  const Heading = isPage ? 'h1' : 'h2';
  useDismissOnEscape(dismiss?.onDismiss);
  const dismissable = dismiss !== undefined;
  // In place, the panel stays beside Sean's composer: while it holds a
  // message, that message is what the reload would lose, so the state asks
  // for it by name, and changes back once it is sent or cleared.
  const holdsMessage = useHasUnsentFolioMessage();
  const reloadHold = hold === 'in-place' && holdsMessage ? 'folio-message' : hold;

  // A failed page replaces the one the user navigated to, so focus follows it
  // there. A panel sits beside work in progress and leaves focus alone, unless
  // the panel covers a page that went inert (Sean on a phone): focus left on
  // an inert control gets no key events, so Escape could not close it.
  useEffect(() => {
    if (isPage || (dismissable && isFocusStranded())) {
      headingRef.current?.focus();
    }
  }, [isPage, dismissable]);

  const reloadButton = (
    <Button
      type="button"
      size={isPage ? 'lg' : 'sm'}
      variant={failure === 'error' ? 'ghost' : 'default'}
      disabled={reloading}
      onClick={onReload}
    >
      {reloading ? <Spinner data-icon="inline-start" aria-hidden="true" /> : null}
      {reloading ? 'Reloading…' : 'Reload'}
    </Button>
  );

  const state = (
    <section
      role="alert"
      aria-labelledby={headingId}
      aria-describedby={descriptionId}
      className={cn(
        'flex flex-col items-center justify-center text-center animate-ca-enter-up-4',
        isPage ? 'min-h-full flex-1 gap-6 px-6 py-16' : 'min-h-0 flex-1 gap-4 px-6 py-10',
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'flex items-center justify-center rounded-full',
          isPage ? 'size-12' : 'size-10',
          copy.iconTone,
        )}
      >
        <Icon className="size-5" />
      </span>
      <div className={cn('flex flex-col', isPage ? 'max-w-md gap-2' : 'max-w-xs gap-1.5')}>
        <Heading
          ref={headingRef}
          id={headingId}
          tabIndex={-1}
          className={cn(
            'font-heading font-bold tracking-tight text-foreground outline-none',
            isPage ? 'text-xl' : 'text-base',
          )}
        >
          {copy.title}
        </Heading>
        <p
          id={descriptionId}
          className={cn(
            'text-muted-foreground',
            isPage ? 'text-sm font-light leading-relaxed' : 'text-xs',
          )}
        >
          {describe(failure, copy, reloadHold)}
        </p>
      </div>
      <div className="flex flex-wrap justify-center gap-2">
        {failure === 'error' && onRetry !== undefined ? (
          <Button type="button" size={isPage ? 'lg' : 'sm'} onClick={onRetry}>
            Retry
          </Button>
        ) : null}
        {reloadButton}
      </div>
    </section>
  );

  if (!isPage) {
    // The panel's own Close lives in the chunk that failed, so the state
    // brings one, in the same place as the panel header's.
    return (
      <div className="flex h-full flex-col">
        {dismiss === undefined ? null : (
          <div className="flex shrink-0 justify-end px-4 py-3">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label={dismiss.label}
              onClick={dismiss.onDismiss}
            >
              <XIcon data-icon />
            </Button>
          </div>
        )}
        {state}
      </div>
    );
  }

  if (!outsideShell) {
    return state;
  }

  // Outside the shell (sign-in, onboarding) the state is the whole screen,
  // so it carries the brand the shell would otherwise show.
  return (
    <div className="flex min-h-svh flex-col bg-secondary text-foreground">
      <header className="px-6 pt-6 lg:px-10">
        <BrandLockup />
      </header>
      {state}
    </div>
  );
}

interface ErrorRecoveryBoundaryProps {
  readonly children: ReactNode;
  /** A new value clears a caught error, such as the location once the user navigates. */
  readonly resetKey: string;
  readonly surface: RecoverySurface;
  /** A panel the user can close offers Close and Escape in its recovery state too. */
  readonly dismiss?: RecoveryDismiss | undefined;
}

interface ErrorRecoveryBoundaryState {
  readonly failure: Failure | null;
  readonly reloading: boolean;
  readonly hold: ReloadHold;
}

const RECOVERED: ErrorRecoveryBoundaryState = { failure: null, reloading: false, hold: 'none' };

function failureState(error: Error): ErrorRecoveryBoundaryState {
  // The page being navigated to: the reload goes ahead unless
  // componentDidCatch finds it held back, and corrects the state before paint.
  if (error instanceof PageChunkLoadError) {
    return { failure: 'new-version', reloading: true, hold: 'none' };
  }
  // Loaded in place, beside work that may not be saved: the user reloads.
  if (isChunkLoadError(error)) {
    return { failure: window.navigator.onLine ? 'new-version' : 'offline', reloading: false, hold: 'in-place' };
  }
  return { failure: 'error', reloading: false, hold: 'none' };
}

/** The state for a navigation whose automatic reload did not go ahead. */
function heldBackState(outcome: Exclude<ChunkReloadOutcome, 'reloading'>): ErrorRecoveryBoundaryState {
  return {
    failure: outcome === 'offline' ? 'offline' : 'new-version',
    reloading: false,
    // Being offline decides before the message does, but the reload the user
    // makes once reconnected would still discard it.
    hold: hasUnsentFolioMessage() ? 'folio-message' : 'none',
  };
}

/**
 * Keeps a failed lazy chunk or render error inside the region that owns it,
 * with a way back, instead of unmounting the app to a blank page. When the
 * page the user is navigating to fails to load after a deploy, the tab
 * reloads once to pick up the new build (see `reloadForNewBuild`), and the
 * state explains the reload while it happens or offers it when held back.
 * Code that fails to load in place never reloads the tab by itself.
 */
export class ErrorRecoveryBoundary extends Component<
  ErrorRecoveryBoundaryProps,
  ErrorRecoveryBoundaryState
> {
  override state: ErrorRecoveryBoundaryState = RECOVERED;

  static getDerivedStateFromError(error: Error): ErrorRecoveryBoundaryState {
    return failureState(error);
  }

  override componentDidCatch(error: Error): void {
    if (!(error instanceof PageChunkLoadError)) {
      return;
    }
    const outcome = reloadForNewBuild();
    if (outcome !== 'reloading') {
      this.setState(heldBackState(outcome));
    }
  }

  override componentDidUpdate(previous: ErrorRecoveryBoundaryProps): void {
    if (this.state.failure !== null && previous.resetKey !== this.props.resetKey) {
      this.setState(RECOVERED);
    }
  }

  private readonly retry = (): void => {
    this.setState(RECOVERED);
  };

  private readonly reload = (): void => {
    this.setState({ reloading: true });
    reloadApp();
  };

  override render(): ReactNode {
    const { failure, reloading, hold } = this.state;
    if (failure === null) {
      return this.props.children;
    }
    return (
      <RecoveryState
        failure={failure}
        reloading={reloading}
        hold={hold}
        surface={this.props.surface}
        dismiss={this.props.dismiss}
        onRetry={this.retry}
        onReload={this.reload}
      />
    );
  }
}

/**
 * The state for code that failed to load in place (`isChunkLoadError`),
 * for a boundary that treats other failures its own way, such as Sean's
 * answers: a new version (or offline), and a Reload the user presses once
 * they have saved. It never reloads by itself.
 */
export function InPlaceChunkRecovery({ surface }: { readonly surface: RecoverySurface }) {
  const [failure] = useState<Failure>(() => (window.navigator.onLine ? 'new-version' : 'offline'));
  const [reloading, setReloading] = useState(false);
  return (
    <RecoveryState
      failure={failure}
      reloading={reloading}
      hold="in-place"
      surface={surface}
      dismiss={undefined}
      onReload={() => {
        setReloading(true);
        reloadApp();
      }}
    />
  );
}

/**
 * One boundary per routed page, cleared by every navigation, so a failed page
 * leaves the shell working. The location key rather than the path clears it,
 * because results differ only in `?template=` and history moves between them.
 */
export function PageErrorBoundary({ children }: { readonly children: ReactNode }) {
  const { key } = useLocation();
  return (
    <ErrorRecoveryBoundary resetKey={key} surface="page">
      {children}
    </ErrorRecoveryBoundary>
  );
}
